import { WORLD } from '../core/config';
import type { BridgeDeck } from './types';
import type { Vec2 } from '../geo/types';
import { pointInPolygon } from '../geo/polygon';
import { WATER_GATE_DECK } from './waterGateDeck';

// Reconstruction heights, not surveyed elevations. These long crossings are visibly raised
// on braced timber bents in the 1538 painting; smaller crossings retain their existing levels.
const RAISED = new Set(['bridge-papenbrug', 'bridge-street-oudebrugsteeg-3', 'bridge-lange-brug']);
export const TIMBER_BRIDGE = { crest: 2.4, approach: 12, thickness: 0.3 } as const;
export const NIEUWE_BRUG = { crest: 4.2, approach: 15 } as const;
// The painted outer Sint Antoniesdijk crossing is raised above its water portal.
// Rise and ramp lengths are fitted reconstruction estimates, not surveyed levels.
export const DIKE_BRIDGE = { crest: 2, approach: 8 } as const;
export const DIKE_BRIDGE_ID = 'bridge-gate-blokhuis-op-de-dijk';
// The two stone arches on the Oudezijds burgwallen hump over their vaults, as a boat needs headroom
// under a vault springing at the water between low quays. Rise and ramps are estimates.
export const BURGWAL_ARCH = { crest: 1.2, approach: 6 } as const;
const BURGWAL_ARCHES = new Set(['bridge-way-38314621', 'bridge-way-38314629']);

export function bridgeProfile(bridge: BridgeDeck) {
  const dx = bridge.b[0] - bridge.a[0];
  const dy = bridge.b[1] - bridge.a[1];
  const length = Math.hypot(dx, dy);
  const axis: Vec2 = [dx / length, dy / length];
  const along = (p: Vec2) => (p[0] - bridge.a[0]) * axis[0] + (p[1] - bridge.a[1]) * axis[1];
  const points = bridge.deck.flatMap((d) => d.outer.map(along));
  const from = Math.min(...points);
  const to = Math.max(...points);
  const bank = bridge.bankY ?? [WORLD.groundY, WORLD.groundY];
  const base = (x: number) =>
    bank[0] + (bank[1] - bank[0]) * Math.max(0, Math.min(1, (x - from) / (to - from)));
  const mouth = bridge.id === 'bridge-martelaarsgracht-mouth';
  const masonry = bridge.id === 'bridge-nieuwe-brug';
  const dike = bridge.id === DIKE_BRIDGE_ID;
  const molStone = bridge.id === 'bridge-molsteeg-torensteeg';
  const molTimber = bridge.id === 'bridge-molsteeg-gravenstraat';
  const burgwalArch = BURGWAL_ARCHES.has(bridge.id);
  if (bridge.id === 'bridge-way-38316242') {
    return {
      axis,
      along,
      from,
      to,
      raised: true,
      knots: [from, to],
      height: (x: number) => base(x) + WATER_GATE_DECK.height,
    };
  }
  const raised =
    (RAISED.has(bridge.id) || masonry || mouth || dike || molStone || molTimber || burgwalArch) &&
    points.length > 0;
  // The two painted Molsteeg crossings have different estimated profiles.
  const settings = molStone
    ? { crest: 1.6, approach: 4 }
    : molTimber
      ? { crest: 0.9, approach: 5 }
      : masonry
        ? NIEUWE_BRUG
        : mouth
          ? { crest: 1.1, approach: 8 }
          : dike
            ? DIKE_BRIDGE
            : burgwalArch
              ? BURGWAL_ARCH
              : TIMBER_BRIDGE;
  // On a skew bank, a ramp starting at the outermost corner produces a step where
  // the middle of the road first becomes wet. The dike landings remain level across
  // their complete bank-clipped ends before either approach rises.
  const middle = (from + to) / 2;
  const bankLevelEnds = dike || molStone || molTimber;
  const west = bankLevelEnds
    ? Math.max(...points.filter((x) => x < middle))
    : masonry
      ? Math.max(from, bridge.westLandingEnd ?? from)
      : from;
  const east = bankLevelEnds ? Math.min(...points.filter((x) => x > middle)) : to;
  const ramp = Math.min(settings.approach, (east - west) / 3);
  const knots = raised
    ? [...new Set([from, west, west + ramp, east - ramp, east, to])]
    : [from, to];
  const height = (x: number) =>
    base(x) +
    (raised ? settings.crest * Math.max(0, Math.min(1, (x - west) / ramp, (east - x) / ramp)) : 0);
  return {
    axis,
    along,
    from,
    to,
    raised: raised || bank.some((y) => y !== WORLD.groundY),
    knots,
    height,
  };
}

export function bridgeHeightAt(bridge: BridgeDeck, p: Vec2): number | null {
  if (!bridge.deck.some((d) => pointInPolygon(p, d))) return null;
  const profile = bridgeProfile(bridge);
  return profile.height(profile.along(p));
}
